import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { site } from "@/content/site";
import { Logo } from "@/components/Logo";

const EASE: [number, number, number, number] = [0.16, 1, 0.3, 1];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <header data-testid="site-header" className="pointer-events-none fixed inset-x-0 top-0 z-50">
      <div className="relative flex items-start justify-between">
        <Link
          to="/"
          aria-label="EVE — home"
          data-testid="header-logo-link"
          className={`float-chip chip-left pointer-events-auto flex h-16 items-center pl-5 pr-10 transition-shadow duration-500 sm:h-20 sm:pl-6 sm:pr-12 ${
            scrolled ? "header-shadow" : ""
          }`}
        >
          <Logo />
        </Link>

        <nav
          aria-label="Primary"
          className={`float-chip chip-right pointer-events-auto hidden h-16 items-center gap-8 pl-10 pr-12 transition-shadow duration-500 sm:h-20 lg:flex ${
            scrolled ? "header-shadow" : ""
          }`}
        >
          {site.nav.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === "/"}
              className={({ isActive }) =>
                `nav-link font-mono text-[11px] uppercase tracking-[0.25em] transition-colors duration-300 ${
                  isActive ? "nav-link--active text-eve-ink" : "text-eve-silver hover:text-eve-ink"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <button
          type="button"
          data-testid="mobile-menu-toggle"
          aria-expanded={open}
          aria-controls="mobile-menu"
          aria-label={open ? "Close menu" : "Open menu"}
          onClick={() => setOpen((v) => !v)}
          className={`float-chip chip-right pointer-events-auto flex h-16 w-16 items-center justify-center text-eve-ink transition-colors duration-300 hover:text-eve-cyan sm:h-20 sm:w-20 lg:hidden ${
            scrolled ? "header-shadow" : ""
          }`}
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            id="mobile-menu"
            role="dialog"
            aria-modal="true"
            aria-label="Site menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: EASE }}
            className="pointer-events-auto fixed inset-0 top-16 z-40 flex h-[calc(100vh-4rem)] flex-col justify-between overflow-y-auto bg-eve-bg/[0.98] px-6 pb-10 pt-8 sm:top-20 sm:h-[calc(100vh-5rem)] lg:hidden"
          >
            <nav aria-label="Mobile" className="flex flex-col gap-2">
              {site.nav.map((item, i) => (
                <motion.div
                  key={item.path}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.05 + i * 0.05, ease: EASE }}
                >
                  <NavLink
                    to={item.path}
                    end={item.path === "/"}
                    className={({ isActive }) =>
                      `block border-b border-white/[0.07] py-4 font-display text-4xl uppercase tracking-wide transition-colors duration-300 ${
                        isActive ? "text-eve-cyan" : "text-eve-ink hover:text-eve-cyan"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.a
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.45, duration: 0.5 }}
              href={site.links.universe}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-universe-link"
              className="btn-outline mt-8 self-start"
            >
              Join the Universe <ArrowUpRight size={14} />
            </motion.a>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
